import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../../shared/abstractStore';
import { editQuestionSupportingDataQuery } from '../../generated/editQuestionSupportingDataQuery';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { analysisTypeFieldsFragment } from '../../models/AnalysisType';
import { subscriptionFieldsFragment } from '../../models/Subscription';

export interface EditQuestionSupportingDataAsyncLoadOptions extends AsyncLoadOptions {
    subscriptionId?: string | null | undefined,
}

/**
 * Supporting data for EditQuestionnaire that loads static data (e.g. dropdowns) that are not changed by the model being used.
 * @param options
 */
export function useEditQuestionSupportingData(options: EditQuestionSupportingDataAsyncLoadOptions = {}): AsyncLoadResult<editQuestionSupportingDataQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<editQuestionSupportingDataQuery>(
        gql`
        query editQuestionSupportingDataQuery ($subscriptionId: ID!) {
            analysisTypes: analysisTypes(subscriptionId: $subscriptionId) {
                ...analysisTypeFields
            }
            
            subscription: subscription(id: $subscriptionId) {
                ...subscriptionFields
            }
        }

        ${analysisTypeFieldsFragment}
        ${subscriptionFieldsFragment}
        `,
        {
            variables: {
                subscriptionId: options.subscriptionId,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}